import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Fan as Fax, Globe2, Mail, Building2 } from "lucide-react";

export type BranchItem = {
  name: string;
  location?: string;
  address?: string;
  phone?: string;
  mobile?: string;
  fax?: string;
  email?: string;
  coordinates?: string;
};

type DivisionBranchesProps = {
  title?: string;
  subtitle?: string;
  divisionLabel?: string;
  branches: BranchItem[];
};

const DivisionBranches: React.FC<DivisionBranchesProps> = ({
  title = "Our Branches",
  subtitle = "Branch contact details and locations across Oman.",
  divisionLabel,
  branches,
}) => {
  const cleanBranches = branches.filter((b) => Boolean(b && (b.name || b.location)));

  return (
    <section className="bg-white py-10">
      <div className="max-w-7xl mx-auto px-4 space-y-8">
        <div className="rounded-3xl border bg-gray-50 p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              {divisionLabel && (
                <div className="text-xs uppercase tracking-widest text-gray-500">
                  {divisionLabel}
                </div>
              )}
              <h2 className="mt-2 text-3xl font-bold text-gray-900">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-3 max-w-3xl text-sm text-gray-600 leading-relaxed">
                  {subtitle}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="text-xs px-3 py-1 rounded-full border bg-yellow-400 font-medium">
                Salalah
              </span>
              <span className="text-xs px-3 py-1 rounded-full border bg-yellow-400 font-medium">
                Muscat
              </span>
              <span className="text-xs px-3 py-1 rounded-full border bg-yellow-400 font-medium">
                Oman
              </span>
            </div>
          </div>
        </div>

        {cleanBranches.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-amber-300 bg-[#fffaf0] p-12 text-center text-slate-600">
            <Building2 className="mx-auto h-12 w-12 text-amber-500 mb-3 opacity-60" />
            <p className="text-base font-bold text-slate-800">No branch locations configured yet.</p>
            <p className="text-sm text-slate-500 mt-1">Please check back soon or contact our headquarters directly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cleanBranches.map((branch, index) => {
              const name = branch.name || branch.location || `Branch ${index + 1}`;
              return (
                <div
                  key={`${name}-${index}`}
                  className="border border-yellow-300 rounded-2xl overflow-hidden shadow-lg shadow-gray-200/70 bg-[#fffaf0] h-full min-h-[250px] hover:shadow-xl transition-all duration-300 flex flex-col"
                >
                  <div className="bg-white px-5 py-4 flex items-center gap-3 min-h-[40px] border-b border-yellow-200">
                    <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-700 text-white shadow-xl shadow-orange-200 flex items-center justify-center shrink-0">
                      <MapPin className="h-5 w-5" />
                    </div>
                    <h3 className="text-[16px] font-bold text-red-900">{name}</h3>
                  </div>

                  <div className="px-8 py-7 space-y-3 text-sm bg-[#fffaf0] flex-1">
                    {(branch.phone || branch.mobile) && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-blue-600 shrink-0" />
                        <span className="text-gray-700 text-[12px] md:text-[14px]">Tel: {branch.phone || branch.mobile}</span>
                      </div>
                    )}

                    {branch.fax && (
                      <div className="flex items-center gap-2">
                        <Fax className="h-4 w-4 text-green-600 shrink-0" />
                        <span className="text-gray-700 text-[12px] md:text-[14px]">Fax: {branch.fax}</span>
                      </div>
                    )}

                    {branch.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-blue-600 shrink-0" />
                        <a
                          href={`mailto:${branch.email}`}
                          className="text-blue-600 hover:underline text-[12px] md:text-[14px]"
                        >
                          {branch.email}
                        </a>
                      </div>
                    )}

                    {branch.address && (
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                        <span className="text-gray-700 text-[12px] md:text-[14px]">{branch.address}</span>
                      </div>
                    )}

                    {branch.coordinates && (
                      <div className="flex items-start gap-2">
                        <Globe2 className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
                        <span className="text-gray-700 text-[12px] md:text-[14px]">
                          Coordinates: {branch.coordinates}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default DivisionBranches;
